import React, { useState, useCallback, useMemo } from 'react';
import { Printer } from 'lucide-react';
import { ReportBlock } from '../../../types/report-builder';
import { Button } from '../../ui/button';
import ReportCanvas from './ReportCanvas';
import ReportChatPanel from './ReportChatPanel';

const ReportBuilderPage: React.FC = () => {
  const [blocks, setBlocks] = useState<ReportBlock[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleBlocksUpdate = useCallback((newBlocks: ReportBlock[]) => {
    setBlocks(newBlocks);
  }, []);

  const handleRemoveBlock = useCallback((blockId: string) => {
    setBlocks((prev) => prev.filter((b) => b.id !== blockId));
  }, []);

  const handleMoveBlock = useCallback((blockId: string, direction: 'up' | 'down') => {
    setBlocks((prev) => {
      const idx = prev.findIndex((b) => b.id === blockId);
      if (idx === -1) return prev;
      const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (targetIdx < 0 || targetIdx >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[targetIdx]] = [next[targetIdx], next[idx]];
      return next;
    });
  }, []);

  const reportTitle = useMemo(() => {
    const headerBlock = blocks.find((block) => block.type === 'header');
    if (headerBlock && 'title' in headerBlock && typeof headerBlock.title === 'string') {
      return headerBlock.title;
    }
    return 'Analyst Canvas';
  }, [blocks]);

  const handlePrintToPdf = useCallback(() => {
    const originalTitle = document.title;
    document.title = reportTitle;
    window.print();
    window.setTimeout(() => {
      document.title = originalTitle;
    }, 250);
  }, [reportTitle]);

  return (
    <div className="relative flex h-full min-h-0 flex-col overflow-y-auto bg-[var(--go-spring)] print:block print:h-auto print:min-h-0 print:overflow-visible print:bg-white">
      <div className="relative flex min-h-full flex-1 flex-col gap-4 px-3 py-3 md:px-4 md:py-4 xl:px-5 xl:py-5">
        <section className="scroll-mt-6">
          <header className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between print:hidden">
            <div className="max-w-3xl">
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                Reports
              </p>
              <h1 className="font-jakarta text-[clamp(2rem,3vw,3.25rem)] font-extrabold tracking-[-0.04em] text-foreground">
                {reportTitle}
              </h1>
              <p className="mt-2 max-w-2xl text-[13px] leading-[1.75] text-muted-foreground md:text-sm">
                Build the report in chat. The canvas updates as sections are generated.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handlePrintToPdf}
                disabled={blocks.length === 0 || isGenerating}
                className="gap-2 rounded-full px-5"
              >
                <Printer className="h-4 w-4" />
                Print to PDF
              </Button>
            </div>
          </header>
        </section>

        <div className="grid min-h-0 flex-1 gap-4 xl:grid-cols-[minmax(0,1fr)_384px] print:block">
          <section className="min-h-0">
            <ReportCanvas
              blocks={blocks}
              isGenerating={isGenerating}
              onRemoveBlock={handleRemoveBlock}
              onMoveBlock={handleMoveBlock}
            />
          </section>

          <section className="min-h-0 print:hidden">
            <ReportChatPanel
              onBlocksUpdate={handleBlocksUpdate}
              onGeneratingChange={setIsGenerating}
            />
          </section>
        </div>
      </div>
    </div>
  );
};

export default ReportBuilderPage;
